import { Controller } from "@hotwired/stimulus";

// 4-step wizard for creating a DocumentEtude:
//   0. Type & référence — radio cards for ConfigDocEtude templates + ref_doc
//   1. Mode             — "à générer" (no upload) vs "déjà signé" (PDF upload)
//   2. Signature        — signataire (contact_id) + date_signature, with the
//                         file upload zone visible only when mode=signed
//   3. Récap            — synthesis + submit
//
// Validation runs on each `next()` and on submit. The form posts via plain
// Rails (multipart) so on success we redirect to the etude show page.
export default class extends Controller {
  static targets = [
    "form", "step", "progressStep", "progressCurrent", "progressTotal",
    "nextBtn", "prevBtn", "submitBtn", "error",
    // Step 0
    "typeRadio", "refDocField",
    // Step 1
    "modeRadio",
    // Step 2
    "contactField", "dateField", "dateHint",
    "uploadField", "fileField",
    "uploadEmpty", "uploadFilled", "uploadFilename",
    "signatureSubtitle", "generateHint",
    // Récap
    "recapType", "recapRef", "recapMode",
    "recapContact", "recapDate", "recapFile",
  ];

  static values = {
    current: { type: Number, default: 0 },
    total:   { type: Number, default: 4 },
    // URL JSON des contacts de l'étude : quand elle est fournie, le select
    // signataire est repeuplé à chaque ouverture de la modal (la liste rendue
    // au chargement de la page peut être périmée : cache Turbo, contact
    // ajouté à l'étude entre temps).
    contactsUrl: String,
  };

  connect() {
    this.progressTotalTarget.textContent = this.totalValue;
    this._render();
    this._applyMode();

    if (this.hasContactsUrlValue && this.contactsUrlValue) {
      this._onModalOpened = () => this._refreshContacts();
      this._modalEl = this.element.closest(".esm_modal");
      this._modalEl?.addEventListener("cockpit-modal:opened", this._onModalOpened);
    }
  }

  disconnect() {
    if (this._modalEl && this._onModalOpened) {
      this._modalEl.removeEventListener("cockpit-modal:opened", this._onModalOpened);
    }
  }

  // Repeuple le <select> natif des signataires depuis le serveur. Select2
  // relit les <option> du DOM à chaque ouverture du dropdown, donc pas
  // besoin de ré-initialiser — on préserve juste la sélection courante.
  _refreshContacts() {
    fetch(this.contactsUrlValue, {
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((list) => {
        if (!this.hasContactFieldTarget || !Array.isArray(list)) return;
        const sel = this.contactFieldTarget;
        const current = sel.value;
        sel.innerHTML = "";
        sel.append(new Option("", ""));
        list.forEach((c) => {
          const label = c.entreprise ? `${c.label} — ${c.entreprise}` : c.label;
          const opt = new Option(label, String(c.id));
          opt.dataset.label = c.label;
          sel.append(opt);
        });
        if (current && list.some((c) => String(c.id) === current)) sel.value = current;
      })
      .catch(() => {}); // best-effort : on garde la liste rendue côté serveur
  }

  // ---- Navigation ------------------------------------------------------

  next(event) {
    event.preventDefault();
    if (!this._validateCurrent()) return;
    if (this.currentValue >= this.totalValue - 1) return;
    this.currentValue += 1;
    this._render();
  }

  prev(event) {
    event.preventDefault();
    if (this.currentValue <= 0) return;
    this.currentValue -= 1;
    this._render();
  }

  submit(event) {
    event.preventDefault();
    if (!this._validateAll()) return;
    this.submitBtnTarget.disabled = true;
    this.submitBtnTarget.innerHTML = '<i class="fa-solid fa-spinner fa-spin"></i> Création…';
    this.formTarget.submit();
  }

  // ---- Mode handling --------------------------------------------------

  modeChanged() {
    this._applyMode();
  }

  _applyMode() {
    const isSigned = this._currentMode() === "signed";
    if (this.hasUploadFieldTarget) this.uploadFieldTarget.hidden = !isSigned;
    if (this.hasGenerateHintTarget) this.generateHintTarget.hidden = isSigned;
    if (!isSigned && this.hasFileFieldTarget) {
      // Si on bascule en mode "à générer", on vide le champ fichier pour ne
      // pas envoyer un PDF accidentellement.
      this.fileFieldTarget.value = "";
      this._resetFileUI();
    }
    if (this.hasSignatureSubtitleTarget) {
      this.signatureSubtitleTarget.textContent = isSigned
        ? "Qui a signé, à quelle date, et upload du PDF signé."
        : "Qui signe et à quelle date (le document sera généré ensuite).";
    }
  }

  _currentMode() {
    return this.modeRadioTargets.find((r) => r.checked)?.value || null;
  }

  // ---- File upload UX -------------------------------------------------

  fileSelected(event) {
    const file = event.currentTarget.files[0];
    if (!file) {
      this._resetFileUI();
      return;
    }
    if (!this._isPdf(file)) {
      this._showError("Seuls les PDF sont acceptés pour un document signé.");
      this.fileFieldTarget.value = "";
      this._resetFileUI();
      return;
    }
    this._hideError();
    this.uploadEmptyTarget.hidden = true;
    this.uploadFilledTarget.hidden = false;
    this.uploadFilenameTarget.textContent = file.name;
  }

  clearFile(event) {
    event.preventDefault();
    event.stopPropagation();
    this.fileFieldTarget.value = "";
    this._resetFileUI();
  }

  _resetFileUI() {
    if (this.hasUploadEmptyTarget)  this.uploadEmptyTarget.hidden = false;
    if (this.hasUploadFilledTarget) this.uploadFilledTarget.hidden = true;
    if (this.hasUploadFilenameTarget) this.uploadFilenameTarget.textContent = "—";
  }

  _isPdf(file) {
    if (file.type === "application/pdf") return true;
    return /\.pdf$/i.test(file.name);
  }

  // ---- Validation ------------------------------------------------------

  _validateCurrent() {
    this._hideError();
    const step = this.currentValue;
    if (step === 0) {
      const type = this.typeRadioTargets.find((r) => r.checked);
      if (!type) return this._fail("Choisis un type de document.");
      const ref = this.refDocFieldTarget.value.trim();
      if (!ref) return this._fail("Saisis une référence pour identifier le document.", this.refDocFieldTarget);
      return true;
    }
    if (step === 1) {
      if (!this._currentMode()) return this._fail("Choisis un mode : à générer ou déjà signé.");
      return true;
    }
    if (step === 2) {
      if (this._currentMode() === "signed") {
        const file = this.fileFieldTarget.files[0];
        if (!file) return this._fail("Upload le PDF signé pour ce mode.");
        if (!this._isPdf(file)) return this._fail("Le fichier doit être un PDF.");
      }
      return true;
    }
    return true;
  }

  _validateAll() {
    for (let i = 0; i <= this.totalValue - 1; i += 1) {
      this.currentValue = i;
      this._render();
      if (!this._validateCurrent()) return false;
    }
    return true;
  }

  // ---- Rendering -------------------------------------------------------

  _render() {
    this.stepTargets.forEach((s) => {
      s.classList.toggle("esm_wizard__step--active", Number(s.dataset.step) === this.currentValue);
    });
    this.progressStepTargets.forEach((p) => {
      const i = Number(p.dataset.step);
      p.classList.toggle("esm_wizard__progress-step--active", i === this.currentValue);
      p.classList.toggle("esm_wizard__progress-step--done",   i < this.currentValue);
    });
    this.progressCurrentTarget.textContent = this.currentValue + 1;
    this.prevBtnTarget.hidden = this.currentValue === 0;

    const isLast = this.currentValue === this.totalValue - 1;
    this.nextBtnTarget.hidden = isLast;
    this.submitBtnTarget.hidden = !isLast;

    if (isLast) this._fillRecap();
    this._hideError();
  }

  _fillRecap() {
    const type = this.typeRadioTargets.find((r) => r.checked);
    this.recapTypeTarget.textContent = type?.dataset.label || "—";
    this.recapRefTarget.textContent  = this.refDocFieldTarget.value || "—";

    const mode = this.modeRadioTargets.find((r) => r.checked);
    this.recapModeTarget.textContent = mode?.dataset.label || "—";

    const sel = this.contactFieldTarget;
    const opt = sel.selectedOptions[0];
    this.recapContactTarget.textContent =
      sel.value && opt ? (opt.dataset.label || opt.text.split(" — ")[0]) : "Aucun";

    const date = this.dateFieldTarget.value;
    this.recapDateTarget.textContent = date ? this._fmtDate(date) : "Non signé";

    const isSigned = this._currentMode() === "signed";
    if (!isSigned) {
      this.recapFileTarget.textContent = "Aucun (génération à suivre)";
    } else {
      const file = this.fileFieldTarget.files[0];
      this.recapFileTarget.textContent = file ? file.name : "À uploader";
    }
  }

  // ---- Helpers ---------------------------------------------------------

  _fmtDate(iso) {
    const [y, m, d] = iso.split("-");
    return `${d}/${m}/${y}`;
  }

  _fail(msg, focusEl) {
    this._showError(msg);
    focusEl?.focus();
    return false;
  }

  _showError(msg) {
    this.errorTarget.textContent = msg;
    this.errorTarget.hidden = false;
  }

  _hideError() {
    this.errorTarget.hidden = true;
    this.errorTarget.textContent = "";
  }
}
